import type { JobModel, ViewJobDetailsModel, ViewJobThirdPartyApply } from './ApplyButtonTypes';
import type { ButtonSize, ButtonVariant } from '../../../../../../mads/Button';

import { usePassportLoginGate } from './PassportLoginModal';
import { type BoxRef, Button, NewWindow } from '../../../../../../mads';
import { useTheme } from '../../../../../../mads/ThemeProvider';
import { useI18n } from '../../../../../../shared/I18n';
import { useLogErrorOnce } from '../../../../../../shared/logging/useLogErrorOnce';
import { useTrackVisibility } from '../../../../../../shared/logging/visibility-tracker';
import {
    type JstBroadcastSubscription,
    subscribe,
} from '../../../../../../shared/native-modules/tare/jstBroadcast';
import { JSTModalScreen } from '../../../../../../shared/native-modules/tare/JSTModalScreen';
import { openModal } from '../../../../../../shared/native-modules/tare/openModal';
import {
    canShowPushPrompt,
    requestReengagementModal,
    requestSystemPushAuthWithPrimer,
} from '../../../../../../shared/native-modules/tare/pushAuthorization';
import { useTareData } from '../../../../../../shared/native-modules/tare/TareDataContext';
import eventEmitter, { ApplyType, ApplyStatus } from '../../../../../../shared/util/eventEmitter';
import { httpGetJson } from '../../../../../native-module/httpGetJson';
import { navigateView } from '../../../../../native-module/navigateView';
import { updateApplyStartCookie } from '../../../../../native-module/updateApplyStartCookie';
import { useViewjobProctorAllocation } from '../../../../../proctor/__generated__/ViewjobProctorContext';
import { IaBrandedApplyButtonRnTst, MapExternalApplyMigrateTst, MegAccelPostThirdpartyApplyPushPrimerTst } from '../../../../../proctor/__generated__/ViewjobProctorSpecifications';
import { getEnvironmentConfig } from '../../../../config/urls';
import { useDwellTiming } from '../../../../context/DwellTiming';
import {
    useDomainUrl,
    useViewJobDataContext,
    useViewjobOneGraphModel,
    useViewjobSourceUrl,
} from '../../../../context/ViewJobDataContext';
import { useEmployerOutreachInfo } from '../../../../hooks/useEmployerOutreachInfo';
import { useEmployerOutreachSource } from '../../../../hooks/useEmployerOutreachSource';
import { useGFJAutoClickApply } from '../../../../hooks/useGFJAutoClickApply';
import { handleEmployerOutreachApply } from '../EmployerOutreach/utils/handleEmployerOutreachApply';
import { StickyShadow } from '../StickyShadow/StickyShadow';
import { type ButtonDimensions, logApplyButtonShowEvent } from '../utils/applyButtonLogging';

import { RNApplyButtonTypeV3 } from '@indeed/signals-schema/com/indeed/signals/schema/viewjob/RNApplyButtonTypeV3';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Platform, useWindowDimensions } from 'react-native';

async function showPushPromptAfterApply(): Promise<void> {
    try {
        const result = await canShowPushPrompt();
        if (result?.canShowPushPrompt) {
            if (result.authStatus !== 'denied') {
                await requestSystemPushAuthWithPrimer('bottomSheet', 'rnviewjob');
            } else {
                await requestReengagementModal('bottomSheet', 'rnviewjob');
            }
        }
    } catch (e) {
        console.error(e);
    }
}

export type ApplyButtonProps = {
    testID?: string;
    newTabLabel: string;
    isLoading: boolean;
    hasCtaEnticer?: boolean;
    shouldHaveShadow?: boolean;
    viewJobButtonLinkModel: ViewJobThirdPartyApply;
    jobModel: JobModel;
    viewJobDetailsModel: ViewJobDetailsModel;
    hasLoggedSeen: boolean;
    setHasLoggedSeen: (value: boolean) => void;
    buttonSize?: ButtonSize | undefined;
    buttonVariant?: ButtonVariant | undefined;
    onApplyPress?: () => void;
};

export function createApplyLink(linkModelHref: string | undefined, domain: string) {
    if (!linkModelHref) {
        return undefined;
    }
    try {
        // linkModelHref is a full URL
        return new URL(linkModelHref).toString();
    } catch {
        // linkModelHref is a relative URL
        return new URL(linkModelHref, domain).toString();
    }
}

export function ApplyButton({
    testID,
    newTabLabel,
    isLoading,
    viewJobButtonLinkModel,
    jobModel,
    viewJobDetailsModel,
    shouldHaveShadow,
    hasLoggedSeen,
    setHasLoggedSeen,
    buttonSize = 'lg',
    buttonVariant = 'primary',
    onApplyPress,
}: ApplyButtonProps) {
    const theme = useTheme();
    const domain = useDomainUrl();
    const sourceUrl = useViewjobSourceUrl();
    const { key: jobKey, title: jobTitle } = jobModel;
    const { appShareUrl } = viewJobButtonLinkModel;
    const { jobCountry: country, mobvjtk: mobtk } = viewJobDetailsModel;
    const applyLink = createApplyLink(viewJobButtonLinkModel.href, domain);
    const [hasLoggedVisibility, setHasLoggedVisibility] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);
    const buttonRef = useRef<BoxRef>(null);
    const { width: screenWidth, height: screenHeight } = useWindowDimensions();
    const ogModel = useViewjobOneGraphModel();
    const { accountKey, ctk, employerOutreachEngagement } = ogModel ?? {};
    const invite = employerOutreachEngagement?.engagements?.[0];
    const tareData = useTareData();
    const buildEnvironment = tareData?.appModel?.buildEnvironment;
    const environmentConfig = getEnvironmentConfig(buildEnvironment);
    const rezcowBaseUrl = environmentConfig.rezcowBaseUrl;
    const resumeBaseUrl = environmentConfig.resumeBaseUrl;

    const { isFromEmployerOutreach, outreachSource } = useEmployerOutreachSource();
    const { outreachInfo } = useEmployerOutreachInfo(jobKey ?? '') ?? {};
    const { initialData } = useViewJobDataContext();
    const isLoggedIn = tareData?.accountStatus?.isLoggedIn ?? false;

    const i18n = useI18n();
    const isWebViewOpen = useRef(false);
    // Snapshot of isLoginGateActive read inside performApply. The gate value is computed
    // by usePassportLoginGate further down, so we cannot capture it in the performApply
    // closure directly without creating a circular dependency.
    const isLoginGateActiveRef = useRef(false);
    const thirdPartyApplyPushPrimerProctor = useViewjobProctorAllocation(MegAccelPostThirdpartyApplyPushPrimerTst);
    const brandedApplyCtaProctor = useViewjobProctorAllocation(IaBrandedApplyButtonRnTst);
    const isApplyCtaTestActive = brandedApplyCtaProctor.value === IaBrandedApplyButtonRnTst.buckets.Test;
    const externalApplyMigrateProctor = useViewjobProctorAllocation(MapExternalApplyMigrateTst);
    const isExternalApplyMigrateActive = externalApplyMigrateProctor.value === MapExternalApplyMigrateTst.buckets.Test;
    const buttonContent = isApplyCtaTestActive
        ? i18n.gettext('Apply with Indeed')
        : viewJobButtonLinkModel.content;

    const willAppearCallbackRef = useRef<() => void>(undefined);
    willAppearCallbackRef.current = () => {

        if (isWebViewOpen.current) {
            isWebViewOpen.current = false;

            if (jobKey) {
                // External applies open a third-party website in a webview, so we cannot
                // determine whether the user actually completed the application.
                eventEmitter.emit('finishApply', {
                    jobKeys: [jobKey],
                    applyType: ApplyType.EXTERNAL_APPLY,
                    status: ApplyStatus.UNKNOWN,
                });
            }

            // Focus the button after returning from the webview
            if (buttonRef.current) {
                AccessibilityInfo.sendAccessibilityEvent(buttonRef.current, 'focus');
            }

            const isThirdPartyApplyPushPrimerProctorActive = thirdPartyApplyPushPrimerProctor.value === MegAccelPostThirdpartyApplyPushPrimerTst.buckets.Test;
            if (isThirdPartyApplyPushPrimerProctorActive) {
                void showPushPromptAfterApply();
            }
        }
    };

    useEffect(() => {
        let subscription: JstBroadcastSubscription | null = null;
        let isMounted = true;

        const stableCallback = () => {
            willAppearCallbackRef.current?.();
        };

        const setup = async () => {
            const sub = await subscribe('ViewWillAppear', stableCallback);
            if (isMounted) {
                subscription = sub;
            } else {
                // remove subscription if component is unmounted. This prevents a potential memory
                // leak if the component is unmounted before the subscribe promise resolves.
                void sub?.remove();
            }
        };
        void setup();

        return () => {
            isMounted = false;
            void subscription?.remove();
        };
    }, []);

    const { markApplyStart } = useDwellTiming();

    // Mirror of legacy basecamp behavior (jobsearch-ui ViewJobButton.tsx): when the apply
    // click is gated by the Passport login modal, fire the /applystart endpoint with
    // log=1 as an image pixel so the apply intent is recorded server-side at gate time
    // (the server treats log=1 as log-only and skips the third-party redirect).
    const logThirdPartyApplyIntercept = useCallback(() => {
        if (Platform.OS !== 'web' || !applyLink) {
            return;
        }
        const interceptUrl = `${applyLink}${applyLink.includes('?') ? '&' : '?'}log=1`;
        const trackingImage = new globalThis.Image();
        trackingImage.src = interceptUrl;
    }, [applyLink]);

    const logThirdPartyApplyStart = useCallback(() => {
        if (!mobtk) {
            return;
        }
        const baseUrl = Platform.OS === 'web' ? window.location.origin : domain;
        if (!baseUrl) {
            return;
        }
        const thirdPartyApplyLogUrl = new URL('/m/rpc/log/apply-start', baseUrl);
        thirdPartyApplyLogUrl.searchParams.set('showPassportModel', isLoggedIn ? '0' : '1');
        thirdPartyApplyLogUrl.searchParams.set('tk', mobtk);
        thirdPartyApplyLogUrl.searchParams.set(
            'surface',
            initialData?.viewJobDisplay ?? 'MOBILE_STANDALONE',
        );
        thirdPartyApplyLogUrl.searchParams.set('spn', initialData?.sponsored ? '1' : '0');
        // isTrusted is always true for user-initiated clicks in React Native
        thirdPartyApplyLogUrl.searchParams.set('iststd', '1');
        if (Platform.OS === 'web') {
            void fetch(thirdPartyApplyLogUrl.toString());
        } else {
            // On native, route through the native bridge so authenticated cookies/headers
            // are attached. The server marks the job as MAYBE_APPLIED based on this call;
            // the response body is irrelevant so we swallow any error (e.g. non-JSON body).
            void httpGetJson(thirdPartyApplyLogUrl.toString()).catch(() => {});
        }
    }, [mobtk, isLoggedIn, initialData?.viewJobDisplay, initialData?.sponsored, domain]);

    const logThirdPartyButtonLoad = useCallback(() => {
        if (Platform.OS !== 'web' || !mobtk) {
            return;
        }
        const thirdPartyButtonLoadLogUrl = new URL(
            '/m/rpc/log/3p-apply-button-load',
            window.location.origin,
        );
        thirdPartyButtonLoadLogUrl.searchParams.set('tk', mobtk);
        void fetch(thirdPartyButtonLoadLogUrl.toString());
    }, [mobtk]);

    const performApply = useCallback(async () => {
        // Set loading state immediately to prevent double-tap during async operations
        setIsNavigating(true);

        // Timeout fallback: if navigation doesn't complete within 3 seconds, allow retry
        const timeoutId = setTimeout(() => {
            setIsNavigating(false);
        }, 3000);

        try {
            // Mark apply start for dwell timing
            markApplyStart();
            onApplyPress?.();
            // TODO WENG-633 implement logging
            logThirdPartyApplyStart();
            // When the Passport login gate will intercept this click, also fire the
            // /applystart endpoint with log=1 so the apply intent is recorded at gate
            // time rather than only after the user completes sign-in.
            if (isLoginGateActiveRef.current) {
                logThirdPartyApplyIntercept();
            }
            //set apply start cookie
            if (jobKey) {
                void updateApplyStartCookie(jobKey, 'v', domain);
            }

            // Log employer outreach apply if this is an employer outreach job
            if (isFromEmployerOutreach && jobKey && invite && rezcowBaseUrl && resumeBaseUrl) {
                await handleEmployerOutreachApply({
                    invite,
                    rezcowBaseUrl,
                    resumeBaseUrl,
                    jobKey,
                    outreachSource,
                    outreachType: outreachInfo?.outreachType,
                    accountKey,
                    ctk,
                    mobtk,
                });
            }
            if (Platform.OS !== 'web' && applyLink) {
                if (isExternalApplyMigrateActive) {
                    isWebViewOpen.current = true;
                    void openModal(JSTModalScreen.externalApply(applyLink), 'rnviewjob')
                        .then(() => {
                            willAppearCallbackRef.current?.();
                        })
                        .catch(() => {
                            isWebViewOpen.current = false;
                        });
                } else {
                    // Note: the promise here never resolves on iOS, and only resolved on error
                    // on android, so await will prevent the rest of the method from running
                    void navigateView(sourceUrl, applyLink, appShareUrl ? appShareUrl : '', jobTitle);
                    isWebViewOpen.current = true;
                }
            }
        } finally {
            clearTimeout(timeoutId);
            setIsNavigating(false);
        }
    }, [
        sourceUrl,
        applyLink,
        jobKey,
        domain,
        jobTitle,
        appShareUrl,
        markApplyStart,
        isFromEmployerOutreach,
        invite,
        rezcowBaseUrl,
        resumeBaseUrl,
        outreachSource,
        outreachInfo,
        accountKey,
        ctk,
        mobtk,
        logThirdPartyApplyStart,
        logThirdPartyApplyIntercept,
        onApplyPress,
        isExternalApplyMigrateActive,
    ]);

    const { isLoginGateActive, loginGateHref, onPressApply, modal } = usePassportLoginGate({
        applyLink,
        appShareUrl: ogModel?.thirdPartyApply?.appShareUrl,
        isLoggedIn,
        contextualSignInPassportURL:
            ogModel?.thirdPartyApplyCreateAccountModel?.contextualSignInPassportURL ?? '',
        onApply: performApply,
        mobtk,
    });
    isLoginGateActiveRef.current = isLoginGateActive;

    useEffect(() => {
        logThirdPartyButtonLoad();
    }, [logThirdPartyButtonLoad]);

    useLayoutEffect(() => {
        if (!hasLoggedVisibility && jobKey && buttonRef.current) {
            buttonRef.current.measure((_, __, width, height, pageX, pageY) => {
                const windowDimensions: ButtonDimensions = {
                    srcWidth: screenWidth,
                    srcHeight: screenHeight,
                    iaButtonX: pageX,
                    iaButtonY: pageY,
                    iaButtonWidth: width,
                    iaButtonHeight: height,
                };
                logApplyButtonShowEvent(
                    jobKey,
                    RNApplyButtonTypeV3.thirdParty,
                    mobtk,
                    windowDimensions,
                );
                setHasLoggedVisibility(true);
            });
        }
    }, [hasLoggedVisibility, jobKey, mobtk, screenHeight, screenWidth]);

    useTrackVisibility(buttonRef, {
        trackingConfig: {
            provider: 'ViewJob',
            trackingKey: mobtk ?? '',
            componentName: 'ThirdPartyApplyButton',
        },
        onBecomeVisible: () => {
            if (!hasLoggedSeen) {
                setHasLoggedSeen(true);
            }
        },
        enabled: !hasLoggedSeen,
    });

    return (
        <>
            <StickyShadow enabled={shouldHaveShadow}>
                <Button
                    ref={buttonRef}
                    testID={testID}
                    onPress={() => {
                        void onPressApply();
                    }}
                    aria-label={`${buttonContent} (${newTabLabel})`}
                    iconAfter={country !== 'JP' && <NewWindow aria-hidden size="xs" fill={buttonVariant === 'secondary' ? theme.colors.foregroundActionPrimary : '#fff'} />}
                    size={buttonSize}
                    loading={isLoading || isNavigating}
                    href={isLoginGateActive ? loginGateHref : applyLink}
                    hrefAttrs={{ rel: 'nofollow', target: 'blank' }}
                    variant={buttonVariant}
                >
                    {!isLoading && buttonContent}
                </Button>
            </StickyShadow>
            {modal}
        </>
    );
}

type OGWrapperOmitKeys = 'viewJobDetailsModel' | 'jobModel' | 'viewJobButtonLinkModel';
export type OGWrappedApplyButtonProps = Omit<ApplyButtonProps, OGWrapperOmitKeys>;

export function OGWrappedApplyButton({
    testID,
    newTabLabel,
    isLoading,
    shouldHaveShadow,
    hasLoggedSeen,
    setHasLoggedSeen,
    buttonSize,
    buttonVariant = 'primary',
    onApplyPress,
}: OGWrappedApplyButtonProps) {
    const ogModel = useViewjobOneGraphModel();
    const { thirdPartyApply, job } = ogModel ?? {};
    const logErrorOnce = useLogErrorOnce();
    const domain = useDomainUrl();

    const thirdPartyApplyUrl =
        Platform.OS === 'web' && thirdPartyApply?.href
            ? createApplyLink(thirdPartyApply.href, domain) ?? null
            : null;
    useGFJAutoClickApply(
        thirdPartyApplyUrl,
        ogModel?.mobvjtk,
        ogModel?.job?.jobKey,
        ogModel?.autoClickApplyFlag,
    );

    if (ogModel == null || thirdPartyApply == null || job == null) {
        if (!job?.isExpired) {
            logErrorOnce('Apply button missing: ThirdPartyApply button not rendered', {
                jobKey: job?.jobKey,
                hasOgModel: ogModel != null,
                hasThirdPartyApply: thirdPartyApply != null,
                hasJob: job != null,
            });
        }
        return null;
    }

    const jobModel: JobModel = {
        key: job.jobKey,
        title: job.details?.title,
    };
    const viewJobDetailsModel: ViewJobDetailsModel = {
        mobvjtk: ogModel.mobvjtk,
        jobCountry: ogModel.job.jobCountry,
    };

    return (
        <ApplyButton
            testID={testID}
            newTabLabel={newTabLabel}
            isLoading={isLoading}
            viewJobButtonLinkModel={thirdPartyApply}
            jobModel={jobModel}
            viewJobDetailsModel={viewJobDetailsModel}
            shouldHaveShadow={shouldHaveShadow}
            hasLoggedSeen={hasLoggedSeen}
            setHasLoggedSeen={setHasLoggedSeen}
            buttonSize={buttonSize}
            buttonVariant={buttonVariant}
            onApplyPress={onApplyPress}
        />
    );
}
